import { trpc } from '@/lib/trpc';

/**
 * Who the session cookie says we are.
 *
 * React Query is the single source of truth here rather than a store of our own: `auth.me` is
 * the only thing that can answer the question (the cookie is httpOnly and unreadable from JS),
 * so a mirrored copy could only ever be a stale second opinion. Mutations that change the
 * session invalidate this query instead.
 *
 * `staleTime` is a ceiling on how long a *quiet* tab will believe an answer, and nothing more:
 * a mutation that changes who this is goes through `onIdentityChanged`, which resets the whole
 * cache and refetches. So no identity change anywhere waits for this window to expire.
 */
export function useSession() {
  const query = trpc.auth.me.useQuery(undefined, {
    staleTime: 30_000,
    // Anonymous is an answer, not a failure — see the `me` procedure. Retrying is pure latency.
    retry: false,
  });

  return {
    user: query.data?.user ?? null,
    // Which ways in this deployment actually offers, and — for the native shell, which hands it
    // to the OS account picker — the web client id Google sign-in runs on. Off and null until
    // the server says otherwise, so a provider button is drawn only once it is known to lead
    // somewhere.
    authProviders: query.data?.authProviders ?? { google: false, googleClientId: null },
    isLoading: query.isLoading,
  };
}
